import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { SignOut } from '@platform-frontend/account';
import { PLATFORM_ENVIRONMENT } from '@platform-frontend/platform';
import { InvalidStateError, RefreshWindow, ShowErrorNotification, convertToHttpParams, handleErrors } from '@platform-frontend/util';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { MARKETPLACE_ERROR_HANDLERS } from '../marketplace-error-handlers';
import {
  MSG_CREATE_UPDATE_BILLING_ADDRESS_COUNTRY_INVALID,
  MSG_CREATE_UPDATE_BILLING_ADDRESS_DEFAULT,
  MSG_CREATE_UPDATE_BILLING_ADDRESS_INTERNAL_VAT_CALCULATION_ERROR,
  MSG_CREATE_UPDATE_BILLING_ADDRESS_INVALID_VAT_ID,
  MSG_CREATE_UPDATE_BILLING_ADDRESS_NOT_FOUND,
  MSG_CREATE_UPDATE_BILLING_ADDRESS_OUTDATED_BILLING_INFORMATION_VERSION,
  MSG_CREATE_UPDATE_BILLING_ADDRESS_TRANSACTION_WITH_SOLUTION_ID_IN_USE,
  MSG_CREATE_UPDATE_BILLING_ADDRESS_VAT_SERVICE_NOT_REACHABLE,
  MSG_CREATE_UPDATE_BILLING_ADDRESS_WRONG_PRICING_INFORMATION,
  MSG_DELETE_BILLING_ADDRESS_DEFAULT,
  MSG_DELETE_BILLING_ADDRESS_NOT_FOUND,
  MSG_DELETE_BILLING_ADDRESS_OUTDATED_BILLING_INFORMATION_VERSION,
  MSG_DELETE_BILLING_ADDRESS_TRANSACTION_WITH_SOLUTION_ID_IN_USE
} from '../notification-messages';
import { BillingInformationService } from './billing-information.service';
import {
  BillingAddressCreated,
  BillingAddressDeleted,
  BillingAddressUpdated,
  CreateBillingAddress,
  DeleteBillingAddress,
  FetchBillingAddresses,
  ResetBillingAddresses,
  SetBillingAddresses,
  UpdateBillingAddress
} from './marketplace-billing-information.actions';
import { BillingAddress } from './model/billing-address';
import { BillingAddressWithIndex } from './model/billing-address-with-index';
import { MarketplaceBillingInformationStateModel } from './model/marketplace-billing-information-state-model';

const DEFAULT_STATE: MarketplaceBillingInformationStateModel = {
  billingAddresses: [],
  accountingEmail: null
};

@State<MarketplaceBillingInformationStateModel>({
  name: 'marketplaceBillingInformation',
  defaults: DEFAULT_STATE
})
@Injectable()
export class MarketplaceBillingInformationState {
  constructor(private billingInformationService: BillingInformationService, private http: HttpClient) {}

  @Selector()
  static billingAddresses(state: MarketplaceBillingInformationStateModel): BillingAddress[] {
    return state.billingAddresses;
  }

  @Selector()
  static accountingEmail(state: MarketplaceBillingInformationStateModel): string | null {
    return state.accountingEmail;
  }

  @Action(SignOut)
  @Action(ResetBillingAddresses)
  resetBillingAddresses(ctx: StateContext<MarketplaceBillingInformationStateModel>): void {
    ctx.setState(DEFAULT_STATE);
  }

  @Action(SetBillingAddresses)
  setBillingAddresses(ctx: StateContext<MarketplaceBillingInformationStateModel>, { billingAddressesWithAccountingEmail }: SetBillingAddresses): void {
    ctx.patchState(billingAddressesWithAccountingEmail);
  }

  @Action(FetchBillingAddresses)
  fetchBillingAddresses(ctx: StateContext<MarketplaceBillingInformationStateModel>, { contractId }: FetchBillingAddresses): Observable<unknown> {
    return this.billingInformationService
      .fetchBillingAddresses(contractId)
      .pipe(switchMap(billingAddressesWithAccountingEmail => ctx.dispatch(new SetBillingAddresses(billingAddressesWithAccountingEmail))));
  }

  @Action(CreateBillingAddress)
  createBillingAddress(ctx: StateContext<MarketplaceBillingInformationStateModel>, { billingAddress }: CreateBillingAddress): Observable<unknown> {
    return this.http.post<BillingAddress>(PLATFORM_ENVIRONMENT.apiUrl + '/billingInformation', billingAddress).pipe(
      switchMap(createdBillingAddress => this.createBillingAddressLocally(ctx, createdBillingAddress).pipe(map(() => createdBillingAddress))),
      this.handleCreateOrUpdateBillingAddressError,
      switchMap(createdBillingAddress => ctx.dispatch(new BillingAddressCreated(createdBillingAddress)))
    );
  }

  private createBillingAddressLocally(ctx: StateContext<MarketplaceBillingInformationStateModel>, billingAddress: BillingAddress): Observable<unknown> {
    const { billingAddresses, accountingEmail } = ctx.getState();
    return ctx.dispatch(new SetBillingAddresses({ billingAddresses: [billingAddress, ...billingAddresses], accountingEmail }));
  }

  @Action(UpdateBillingAddress)
  updateBillingAddress(
    ctx: StateContext<MarketplaceBillingInformationStateModel>,
    { billingAddress, overrideSubscription }: UpdateBillingAddress
  ): Observable<unknown> {
    return this.http
      .put<BillingAddress>(PLATFORM_ENVIRONMENT.apiUrl + '/billingInformation', { ...billingAddress, contractOrProductWithPrice: overrideSubscription })
      .pipe(
        switchMap(updatedBillingAddress => this.updateBillingAddressLocally(ctx, billingAddress, updatedBillingAddress).pipe(map(() => updatedBillingAddress))),
        this.handleCreateOrUpdateBillingAddressError,
        switchMap(updatedBillingAddress => ctx.dispatch(new BillingAddressUpdated(billingAddress, updatedBillingAddress)))
      );
  }

  private updateBillingAddressLocally(
    ctx: StateContext<MarketplaceBillingInformationStateModel>,
    original: BillingAddress,
    update: BillingAddress
  ): Observable<unknown> {
    const { billingAddresses, accountingEmail } = ctx.getState();
    const { index } = this.getBillingAddressWithIndex(ctx, original.id);
    return ctx.dispatch(
      new SetBillingAddresses({ billingAddresses: [update, ...billingAddresses.slice(0, index), ...billingAddresses.slice(index + 1)], accountingEmail })
    );
  }

  private handleCreateOrUpdateBillingAddressError = (obs: Observable<BillingAddress>): Observable<BillingAddress> =>
    obs.pipe(
      handleErrors(MARKETPLACE_ERROR_HANDLERS, {
        TRANSACTION_WITH_SOLUTION_ID_IN_USE: new ShowErrorNotification(MSG_CREATE_UPDATE_BILLING_ADDRESS_TRANSACTION_WITH_SOLUTION_ID_IN_USE),
        OUTDATED_BILLING_INFORMATION_VERSION: new RefreshWindow(MSG_CREATE_UPDATE_BILLING_ADDRESS_OUTDATED_BILLING_INFORMATION_VERSION),
        WRONG_PRICING_INFORMATION: new ShowErrorNotification(MSG_CREATE_UPDATE_BILLING_ADDRESS_WRONG_PRICING_INFORMATION),
        INVALID_VAT_ID: new ShowErrorNotification(MSG_CREATE_UPDATE_BILLING_ADDRESS_INVALID_VAT_ID),
        VAT_SERVICE_NOT_REACHABLE: new ShowErrorNotification(MSG_CREATE_UPDATE_BILLING_ADDRESS_VAT_SERVICE_NOT_REACHABLE),
        COUNTRY_INVALID: new ShowErrorNotification(MSG_CREATE_UPDATE_BILLING_ADDRESS_COUNTRY_INVALID),
        INTERNAL_VAT_CALCULATION_ERROR: new ShowErrorNotification(MSG_CREATE_UPDATE_BILLING_ADDRESS_INTERNAL_VAT_CALCULATION_ERROR),
        NOT_FOUND: new RefreshWindow(MSG_CREATE_UPDATE_BILLING_ADDRESS_NOT_FOUND),
        DEFAULT: new ShowErrorNotification(MSG_CREATE_UPDATE_BILLING_ADDRESS_DEFAULT)
      })
    );

  @Action(DeleteBillingAddress)
  deleteBillingAddress(ctx: StateContext<MarketplaceBillingInformationStateModel>, { billingAddressId }: DeleteBillingAddress): Observable<unknown> {
    const billingAddressWithIndex = this.getBillingAddressWithIndex(ctx, billingAddressId);
    const params = convertToHttpParams({
      billingInformationId: billingAddressId,
      serversideVersion: billingAddressWithIndex.billingAddress.serversideVersion
    });

    return this.http.delete<void>(PLATFORM_ENVIRONMENT.apiUrl + '/billingInformation', { params }).pipe(
      switchMap(() => this.deleteBillingAddressLocally(ctx, billingAddressWithIndex.index)),
      switchMap(() => ctx.dispatch(new BillingAddressDeleted(billingAddressWithIndex.billingAddress))),
      handleErrors(MARKETPLACE_ERROR_HANDLERS, {
        TRANSACTION_WITH_SOLUTION_ID_IN_USE: new ShowErrorNotification(MSG_DELETE_BILLING_ADDRESS_TRANSACTION_WITH_SOLUTION_ID_IN_USE),
        OUTDATED_BILLING_INFORMATION_VERSION: new RefreshWindow(MSG_DELETE_BILLING_ADDRESS_OUTDATED_BILLING_INFORMATION_VERSION),
        NOT_FOUND: new RefreshWindow(MSG_DELETE_BILLING_ADDRESS_NOT_FOUND),
        DEFAULT: new ShowErrorNotification(MSG_DELETE_BILLING_ADDRESS_DEFAULT)
      })
    );
  }

  private deleteBillingAddressLocally(ctx: StateContext<MarketplaceBillingInformationStateModel>, index: number): Observable<unknown> {
    const { billingAddresses, accountingEmail } = ctx.getState();
    return ctx.dispatch(
      new SetBillingAddresses({ billingAddresses: [...billingAddresses.slice(0, index), ...billingAddresses.slice(index + 1)], accountingEmail })
    );
  }

  private getBillingAddressWithIndex(ctx: StateContext<MarketplaceBillingInformationStateModel>, billingAddressId: number): BillingAddressWithIndex {
    const addresses = ctx.getState().billingAddresses;
    const index = addresses.findIndex(billingAddress => billingAddress.id === billingAddressId);

    if (index > -1) {
      return { billingAddress: addresses[index], index };
    } else {
      throw new InvalidStateError(`Billing address with id ${billingAddressId} does not exist`);
    }
  }
}
